import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { BaseItemsParamsDto, BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { TestCaseDto } from 'src/app/models/dto/testCaseDto';
import { normalizeArpyLinksFromGraphql } from 'src/app/services/helpers/arpy/arpy-link.helper';
import { formField } from 'src/app/models/formField';;
import { GETALL_TEST_CASES_QUERY, GETALL_TEST_CASES_QUERY_PAGINATED, GET_TEST_CASE_QUERY, GET_TEST_CASE_SEARCH_UI_TEMPLATE_QUERY, GET_TEST_CASE_UI_TEMPLATE_QUERY, SEARCH_TEST_CASE_QUERY } from 'src/app/graphQL/queries/test-case-queries';
import { ApolloClientService } from './apollo-client.service';
import { GraphQLResponseOutput } from 'src/app/graphQL/models/output/graphql-response-output';
import { GraphQLFormFieldOutput } from 'src/app/graphQL/models/output/graphql-form-field-output';
import { convertBaseItemsParamsDtoToGraphqlInput, convertGraphqlBaseTestItemToDto, convertGraphqlFormFieldToDto, convertGraphqlPaginatedBaseItemToDto, convertGraphqlResponseToDto, convertGraphqlResponseToDtoSimpleReturnValue } from 'src/app/graphQL/mappers/common.mappers';
import { CREATE_TEST_CASE_MUTATION, DELETE_TEST_CASE_MUTATION, IMPORT_TEST_CASES_MUTATION, UPDATE_TEST_CASE_MUTATION } from 'src/app/graphQL/mutations/test-case-mutations';
import { GraphQLBaseTestItemOutput, GraphQLPaginatedBaseTestItemOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { convertTestCaseDtoToGraphqlInput } from 'src/app/graphQL/mappers/test-case.mappers';
import { environment } from 'src/environments/environment';
import { Guid } from 'src/app/constants/guidConstants';
import { PaginatedItemsResponseDto } from 'src/app/models/dto/paginatedItemsResponseDto';
import { DualApiService, Scope } from '../migration/dual-api.service';

@Injectable({
  providedIn: 'root'
})
export class TestCaseApiService {

  constructor(private apolloClientService: ApolloClientService, private dualApiService: DualApiService) { }

  getAllPaginatedPages(baseItemsParams: BaseItemsParamsDto): Observable<PaginatedItemsResponseDto> {
    if(environment.IsHero && !baseItemsParams.regBy)
      baseItemsParams.orgGroupId = Guid.HERO;

    const graphqlBaseItemsParams = convertBaseItemsParamsDtoToGraphqlInput(baseItemsParams)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestCasesPagination : GraphQLPaginatedBaseTestItemOutput}>({
          query: GETALL_TEST_CASES_QUERY_PAGINATED,
          variables: {BaseInput: graphqlBaseItemsParams }
        })
      ),
      map(results => {
        const testCases = results.data?.configuratorTestCasesPagination
        if (!testCases)
          return undefined

        var result = convertGraphqlPaginatedBaseItemToDto(testCases);
        return result;
      })
    );

    // Scope is server-resolved from caller identity on the Go Lambda. The client picks
    // the intent (MINE / MY_GROUP) based on which BaseItemsParams field is populated;
    // the lambda fills in the username / group list and the client doesn't need to send them.
    let scope: Scope | undefined;
    if (baseItemsParams.regBy) scope = 'MINE';
    else if (baseItemsParams.orgGroupId) scope = 'MY_GROUP';

    return this.dualApiService.dualGetAllPaginated('testCases', baseItemsParams.currentPage, baseItemsParams.pageSize, [], oldApiCall$, scope);
  }

  // getAll(company: string, regBy: string, functionalGroupId?: string): Observable<BaseTestItemDto[]> {
  //   return this.apolloClientService.apolloClient$.pipe(
  //     switchMap(client =>
  //       client.query<{configuratorAllTestCases : GraphQLBaseTestItemOutput[]}>({
  //         query: GETALL_TEST_CASES_QUERY,
  //         variables: {Company: company, RegBy: regBy, OrgGroup: functionalGroupId}
  //       })
  //     ),
  //     map(results => {
  //       const testCases = results.data.configuratorAllTestCases
  //       if (!testCases)
  //         return undefined

  //       return testCases.map(item => {
  //         return convertGraphqlBaseTestItemToDto(item);
  //       })
  //     })
  //   )
  // }

  getByUid(uid: string): Observable<ResponseDto> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestCaseById: GraphQLResponseOutput}>({
          query: GET_TEST_CASE_QUERY,
          variables: {Uid: uid}
        })
      ),
      map(results => {
        const testCase = results.data.configuratorTestCaseById
        if (!testCase)
          return undefined

        const response = convertGraphqlResponseToDto<TestCaseDto>(testCase);
        if (response.returnValue?.arpyLinks) {
          response.returnValue.arpyLinks = normalizeArpyLinksFromGraphql(response.returnValue.arpyLinks);
        }
        return response;
      })
    );

    return this.dualApiService.dualGetByUid('testCases', uid, oldApiCall$);
  }

  getUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestCaseUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_CASE_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testCaseUiTemplate = results.data.configuratorTestCaseUiTemplate
        if (!testCaseUiTemplate)
          return undefined

        return testCaseUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  getSearchUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorTestCaseSearchUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_TEST_CASE_SEARCH_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const testCaseUiTemplate = results.data.configuratorTestCaseSearchUiTemplate

        if (!testCaseUiTemplate)
          return undefined

        return testCaseUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  create(testCase: TestCaseDto): Observable<ResponseDto> {
    const graphqlTestCaseInput = convertTestCaseDtoToGraphqlInput(testCase)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorCreateTestCase: GraphQLResponseOutput}>({
          mutation: CREATE_TEST_CASE_MUTATION,
          variables: {TestCase: graphqlTestCaseInput }
        })
      ),
      map(result => convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorCreateTestCase as GraphQLResponseOutput))
    );

    return this.dualApiService.dualCreate('testCases', testCase, oldApiCall$);
  }

  update(testCase: TestCaseDto): Observable<ResponseDto> {
    const graphqlTestCaseInput = convertTestCaseDtoToGraphqlInput(testCase)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorUpdateTestCase: GraphQLResponseOutput}>({
          mutation: UPDATE_TEST_CASE_MUTATION,
          variables: {TestCase: graphqlTestCaseInput }
        })
      ),
      map(result => convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorUpdateTestCase as GraphQLResponseOutput))
    );

    return this.dualApiService.dualUpdate('testCases', testCase.uid, testCase, oldApiCall$);
  }

  bulkImport(testCases: TestCaseDto[]): Observable<ResponseDto> {
    const graphqlInputs = testCases.map(tc => convertTestCaseDtoToGraphqlInput(tc));

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{ configuratorImportTestCases: GraphQLResponseOutput }>({
          mutation: IMPORT_TEST_CASES_MUTATION,
          variables: { TestCases: graphqlInputs },
        })
      ),
      map(result => convertGraphqlResponseToDtoSimpleReturnValue(result.data.configuratorImportTestCases as GraphQLResponseOutput))
    );

    return this.dualApiService.dualImport('testCases', testCases, oldApiCall$);
  }

  delete(uid: string, regBy: string): Observable<any> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DELETE_TEST_CASE_MUTATION,
          variables: {Uid: uid}
        })
      ),
      map(result =>(result.data))
    );

    return this.dualApiService.dualDelete('testCases', uid, oldApiCall$);
  }

  search(testCase: TestCaseDto, scope?: Scope): Observable<BaseTestItemDto[]> {
    const graphqlTestCaseInput = convertTestCaseDtoToGraphqlInput(testCase);

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSearchTestCase: GraphQLBaseTestItemOutput[]}>({
          query: SEARCH_TEST_CASE_QUERY,
          variables: {TestCase: graphqlTestCaseInput }
        })
      ),
      map(results => {
        const testCases = results?.data?.configuratorSearchTestCase
        if (!testCases)
          return undefined;

        return testCases.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    return this.dualApiService.dualSearch('testCases', testCase, oldApiCall$, scope);
  }
}
